import { roundMinor } from './money';
const QTY_EPSILON = 1e-6;
export function minorUnitForDigits(digits) {
    return digits === 0 ? 100 : 1;
}
function roundToUnit(value, unit) {
    return unit === 1 ? roundMinor(value) : roundMinor(value / unit) * unit;
}
export function creditableQty(line) {
    const left = line.qty - line.returnedQty - line.creditedQty;
    return left > QTY_EPSILON ? left : 0;
}
function netAfterReturns(line, unit) {
    if (!(line.returnedQty > 0) || line.qty <= 0)
        return { net: line.netMinor, tax: line.taxMinor };
    const share = Math.min(1, line.returnedQty / line.qty);
    return {
        net: line.netMinor - roundToUnit(line.netMinor * share, unit),
        tax: line.taxMinor - roundToUnit(line.taxMinor * share, unit),
    };
}
export function creditLineShare(line, qty, unit = 1) {
    if (!Number.isFinite(qty) || qty <= 0)
        throw new Error('The quantity to credit must be more than zero');
    const available = creditableQty(line);
    if (qty > available + QTY_EPSILON) {
        throw new Error(available === 0
            ? `${line.description} has nothing left to credit`
            : `Only ${Number(available.toFixed(3))} of ${line.description} can still be credited`);
    }
    const base = netAfterReturns(line, unit);
    const remainingNet = Math.max(0, base.net - line.creditedNetMinor);
    const remainingTax = Math.max(0, base.tax - line.creditedTaxMinor);
    const perLineQty = line.qty - line.returnedQty;
    let net;
    let tax;
    if (qty >= available - QTY_EPSILON) {
        net = remainingNet;
        tax = Math.min(remainingTax, net);
    }
    else {
        net = Math.min(remainingNet, roundToUnit((base.net * qty) / perLineQty, unit));
        tax = Math.min(net, remainingTax, roundToUnit((base.tax * qty) / perLineQty, unit));
    }
    if (net <= 0)
        throw new Error(`${line.description} has no charge left to credit`);
    return { invoiceItemId: line.id, description: line.description, qty, netMinor: net, taxMinor: tax };
}
export function planLineCreditNote(lines, requests, unit = 1) {
    if (requests.length === 0)
        throw new Error('Choose at least one line to credit');
    const byId = new Map(lines.map((line) => [line.id, line]));
    const seen = new Set();
    const shares = [];
    for (const request of requests) {
        if (seen.has(request.invoiceItemId))
            throw new Error('A line can only be credited once in the same credit note');
        seen.add(request.invoiceItemId);
        const line = byId.get(request.invoiceItemId);
        if (!line)
            throw new Error('One of the chosen lines is not on this invoice');
        shares.push(creditLineShare(line, request.qty, unit));
    }
    return {
        lines: shares,
        netMinor: shares.reduce((sum, share) => sum + share.netMinor, 0),
        taxMinor: shares.reduce((sum, share) => sum + share.taxMinor, 0),
    };
}
export function planAmountCreditNote(params) {
    const { invoiceNetMinor, invoiceTaxMinor, amountMinor, description } = params;
    const unit = params.unit ?? 1;
    if (!Number.isInteger(amountMinor) || amountMinor <= 0)
        throw new Error('The credit amount must be more than zero');
    if (amountMinor % unit !== 0)
        throw new Error('This currency has no smaller coins; credit a whole amount');
    if (amountMinor > invoiceNetMinor) {
        throw new Error('The credit cannot be more than what the invoice is still worth');
    }
    const tax = invoiceNetMinor > 0 ? Math.min(amountMinor, roundToUnit((amountMinor * invoiceTaxMinor) / invoiceNetMinor, unit)) : 0;
    return {
        lines: [{ invoiceItemId: '', description, qty: 0, netMinor: amountMinor, taxMinor: tax }],
        netMinor: amountMinor,
        taxMinor: tax,
    };
}
export function customerCreditMinor(params) {
    const patientDue = params.netMinor - params.panelShareMinor;
    return Math.max(0, params.paidMinor - Math.max(0, patientDue));
}
//# sourceMappingURL=credit-note.js.map